import { useState, useCallback } from "react";
import { useSelectedOrganisation } from "xplatform_shell/useSelectedOrganisation";
import type {
  ExternalRefUsageItemViewModel,
  ExternalRefUsageFilterParams,
  MonitoringProcessViewModel,
  MonitoringMappingJobViewModel,
  SearchUsageViewModel,
  BatchJobSummaryViewModel,
  ProcessSummaryViewModel,
  ProcessTimeSeriesViewModel,
  CostEventViewModel,
  ExternalRefCapabilityUsageViewModel,
} from "@models/monitoring";
import { type PaginatedResponse } from "@models/common";
import type { IProcessDetailViewModel } from "@models/extraction";
import { costApi } from "@services/costApi";
import { intelligenceEngineApi } from "@services/extractionApi";
import { mappingApi } from "@services/mappingApi";
import { buildSearchUsageViewModels } from "@utils/builders/monitoring";
import { useAdminPermissions } from "./useAdminPermissions";

export interface IMonitoringState {
  externalRefUsage: ExternalRefUsageItemViewModel[];
  monitoringProcesses: MonitoringProcessViewModel[];
  mappingJobs: MonitoringMappingJobViewModel[];
  searchUsage: SearchUsageViewModel[];
  summary: ProcessSummaryViewModel | null;
  timeSeries: ProcessTimeSeriesViewModel | null;
  total: number;
  isLoading: boolean;
  error: string | null;
  fetchExternalRefUsage: (
    filters?: ExternalRefUsageFilterParams,
  ) => Promise<PaginatedResponse<ExternalRefUsageItemViewModel> | null>;
  fetchMonitoringProcesses: (
    filters?: ExternalRefUsageFilterParams,
  ) => Promise<MonitoringProcessViewModel[] | null>;
  fetchMappingJobs: (
    filters?: ExternalRefUsageFilterParams,
  ) => Promise<MonitoringMappingJobViewModel[] | null>;
  fetchSearchUsage: (
    filters?: ExternalRefUsageFilterParams,
  ) => Promise<SearchUsageViewModel[] | null>;
  fetchMappingJobDetail: (
    jobId: string,
  ) => Promise<BatchJobSummaryViewModel | null>;
  fetchProcessSummary: (
    startDate: string,
    endDate: string,
  ) => Promise<ProcessSummaryViewModel | null>;
  fetchProcessTimeSeries: (
    startDate: string,
    endDate: string,
  ) => Promise<ProcessTimeSeriesViewModel | null>;
  fetchDashboardData: (startDate: string, endDate: string) => Promise<void>;
  fetchProcessDetail: (
    processId: string,
  ) => Promise<IProcessDetailViewModel | null>;
  fetchProcessCostEvents: (
    processId: string,
  ) => Promise<CostEventViewModel[] | null>;
  fetchProcessCapabilityUsage: (
    externalRefType: string,
  ) => Promise<ExternalRefCapabilityUsageViewModel[] | null>;
}

export const useMonitoring = (): IMonitoringState => {
  const { isOrgLeader } = useAdminPermissions();
  const { organisation } = useSelectedOrganisation();
  const [externalRefUsage, setExternalRefUsage] = useState<
    ExternalRefUsageItemViewModel[]
  >([]);
  const [monitoringProcesses, setMonitoringProcesses] = useState<
    MonitoringProcessViewModel[]
  >([]);
  const [mappingJobs, setMappingJobs] = useState<
    MonitoringMappingJobViewModel[]
  >([]);
  const [searchUsage, setSearchUsage] = useState<SearchUsageViewModel[]>([]);
  const [summary, setSummary] = useState<ProcessSummaryViewModel | null>(null);
  const [timeSeries, setTimeSeries] =
    useState<ProcessTimeSeriesViewModel | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const organisationId = isOrgLeader ? organisation?.organisationId : undefined;

  const withOrgScope = useCallback(
    (
      filters?: ExternalRefUsageFilterParams,
    ): ExternalRefUsageFilterParams | undefined => {
      if (!organisationId) return filters;
      return { ...filters, organisationId };
    },
    [organisationId],
  );

  const fetchExternalRefUsage = useCallback(
    async (
      filters?: ExternalRefUsageFilterParams,
    ): Promise<PaginatedResponse<ExternalRefUsageItemViewModel> | null> => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await costApi.getExternalRefUsage(
          withOrgScope(filters),
        );
        setExternalRefUsage(response.items);
        setTotal(response.total);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch external reference usage";
        setError(errorMessage);
        console.error("Error fetching external reference usage:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [withOrgScope],
  );

  const fetchMonitoringProcesses = useCallback(
    async (
      filters?: ExternalRefUsageFilterParams,
    ): Promise<MonitoringProcessViewModel[] | null> => {
      try {
        setIsLoading(true);
        setError(null);

        // Single call: core's BFF endpoint returns extraction (PROCESS#) cost
        // usage already enriched with process metadata (status/items/doctype/
        // created_by), so the browser no longer makes a second round trip to
        // the extraction service.
        const apiLimit = Math.min(filters?.limit || 10, 200);
        const response = await costApi.getExtractionUsage(
          withOrgScope({
            ...filters,
            limit: apiLimit,
            offset: filters?.offset || 0,
          }),
        );

        setTotal(response.total);
        setMonitoringProcesses(response.items);
        return response.items;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch monitoring processes";
        setError(errorMessage);
        console.error("Error fetching monitoring processes:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [withOrgScope],
  );

  const fetchMappingJobs = useCallback(
    async (
      filters?: ExternalRefUsageFilterParams,
    ): Promise<MonitoringMappingJobViewModel[] | null> => {
      try {
        setIsLoading(true);
        setError(null);

        // Single call: core's BFF endpoint returns mapping cost usage already
        // enriched with job metadata (status/plugin/rows/created_by), so the
        // browser no longer makes a second round trip to the mapping service.
        const apiLimit = Math.min(filters?.limit || 10, 200);
        const response = await costApi.getMappingUsage(
          withOrgScope({
            ...filters,
            limit: apiLimit,
            offset: filters?.offset || 0,
          }),
        );

        setTotal(response.total);
        setMappingJobs(response.items);
        return response.items;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch mapping jobs";
        setError(errorMessage);
        console.error("Error fetching mapping jobs:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [withOrgScope],
  );

  const fetchSearchUsage = useCallback(
    async (
      filters?: ExternalRefUsageFilterParams,
    ): Promise<SearchUsageViewModel[] | null> => {
      try {
        setIsLoading(true);
        setError(null);

        // Each SEARCH#<correlation_id> row is one request; walk the whole window
        // (200/req cap) so every request in range is listed, not just one page.
        // Backend orders by most-recent-first, so the collected order is kept.
        const PAGE_SIZE = 200;
        const MAX_ROWS = 5000;
        const collected: ExternalRefUsageItemViewModel[] = [];
        let offset = 0;
        for (;;) {
          const usageResponse = await costApi.getExternalRefUsage(
            withOrgScope({
              ...filters,
              serviceName: "search",
              limit: PAGE_SIZE,
              offset,
            }),
          );
          collected.push(...usageResponse.items);
          offset += PAGE_SIZE;
          if (
            !usageResponse.has_more ||
            usageResponse.items.length === 0 ||
            collected.length >= MAX_ROWS
          ) {
            break;
          }
        }

        setExternalRefUsage(collected);
        const requests = buildSearchUsageViewModels(collected);
        setSearchUsage(requests);
        setTotal(requests.length);
        return requests;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch search usage";
        setError(errorMessage);
        console.error("Error fetching search usage:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [withOrgScope],
  );

  const fetchMappingJobDetail = useCallback(
    async (jobId: string): Promise<BatchJobSummaryViewModel | null> => {
      try {
        setIsLoading(true);
        setError(null);
        const jobs = await mappingApi.getJobsBatch([jobId]);
        return jobs.find((job) => job.jobId === jobId) ?? null;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch mapping job detail";
        setError(errorMessage);
        console.error("Error fetching mapping job detail:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const fetchProcessSummary = useCallback(
    async (
      startDate: string,
      endDate: string,
    ): Promise<ProcessSummaryViewModel | null> => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await intelligenceEngineApi.getProcessSummary(
          startDate,
          endDate,
          organisationId,
        );
        setSummary(response);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch process summary";
        setError(errorMessage);
        console.error("Error fetching process summary:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [organisationId],
  );

  const fetchProcessTimeSeries = useCallback(
    async (
      startDate: string,
      endDate: string,
    ): Promise<ProcessTimeSeriesViewModel | null> => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await intelligenceEngineApi.getProcessTimeSeries(
          startDate,
          endDate,
          organisationId,
        );
        setTimeSeries(response);
        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch process time series";
        setError(errorMessage);
        console.error("Error fetching process time series:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [organisationId],
  );

  const fetchDashboardData = useCallback(
    async (startDate: string, endDate: string): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);

        const [summaryResponse, timeSeriesResponse] = await Promise.all([
          intelligenceEngineApi.getProcessSummary(
            startDate,
            endDate,
            organisationId,
          ),
          intelligenceEngineApi.getProcessTimeSeries(
            startDate,
            endDate,
            organisationId,
          ),
        ]);

        setSummary(summaryResponse);
        setTimeSeries(timeSeriesResponse);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch monitoring dashboard data";
        setError(errorMessage);
        console.error("Error fetching monitoring dashboard data:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [organisationId],
  );

  const fetchProcessDetail = useCallback(
    async (processId: string): Promise<IProcessDetailViewModel | null> => {
      try {
        setIsLoading(true);
        setError(null);
        return await intelligenceEngineApi.getProcessDetailWithItems(processId);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch process detail";
        setError(errorMessage);
        console.error("Error fetching process detail:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const fetchProcessCostEvents = useCallback(
    async (processId: string): Promise<CostEventViewModel[] | null> => {
      try {
        setIsLoading(true);
        setError(null);
        return await costApi.getCostEventsByProcessId(processId);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch process cost events";
        setError(errorMessage);
        console.error("Error fetching process cost events:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const fetchProcessCapabilityUsage = useCallback(
    async (externalRefType: string): Promise<ExternalRefCapabilityUsageViewModel[] | null> => {
      try {
        setIsLoading(true);
        setError(null);
        return await costApi.getExternalRefUsageDetail(externalRefType);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch capability usage";
        setError(errorMessage);
        console.error("Error fetching capability usage:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return {
    externalRefUsage,
    monitoringProcesses,
    mappingJobs,
    searchUsage,
    summary,
    timeSeries,
    total,
    isLoading,
    error,
    fetchExternalRefUsage,
    fetchMonitoringProcesses,
    fetchMappingJobs,
    fetchSearchUsage,
    fetchMappingJobDetail,
    fetchProcessSummary,
    fetchProcessTimeSeries,
    fetchDashboardData,
    fetchProcessDetail,
    fetchProcessCostEvents,
    fetchProcessCapabilityUsage,
  };
};
